import { yupResolver } from '@hookform/resolvers/yup';
import { FlexLayout, GridLayout, Spacer } from '@Styled/utilities';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { TypedTestOrderingQuestionPayload } from '../../../Types/TestQuestion';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import {
	Button,
	Checkbox,
	InputAdornment,
	ListItemText,
	MenuItem,
	Typography,
} from '@mui/material';
import { useTypedSelector } from '@Features/store';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import TagChip from '@Components/UI/Chips/TagChip/TagChip';
import { EdFormControlLabel } from '@Components/UI/Inputs/EdFormControlLabel/EdFormControlLabel';
import { useSnackbar } from '@Providers/useSnackbar';
import { useHistory } from 'react-router-dom';
import { TestQuestionsRequester } from '../../../Services/TestQuestions/TestQuestionsRequester';
import OrderingOption from './OrderingOption';
import { ORDERING_SCHEMA } from '../../../Schema/OrderingSchema';
import styled from 'styled-components';
import { useGotoParentRoute } from '@Providers/Routes/RoutesProvider';
import RichTextEditor from '@Components/UI/RichTextEditor/RichTextEditor';
import { UnsavedChangesGuard } from '@RichTextEditor';
import AddTag from '../../AddTag';
import EdButtonMenu from '@Components/UI/Buttons/EdButtonMenu/EdButtonMenu';

const Ordering = () => {
	const editorRef = useRef<any>(null);
	const feedbackEditorRef = useRef<any>(null);
	const [addedId, setAddedId] = useState<number | undefined>();
	const [orderIndex, setOrderIndex] = useState<number>(3);
	const { dropdownTags } = useTypedSelector((state) => state.dropdowns);
	const { currentEntity } = useTypedSelector((state) => state.Tests);
	const { displaySnackbar } = useSnackbar();
	const history = useHistory();
	const goToParent = useGotoParentRoute();
	const { control, watch, formState, handleSubmit, setValue, reset } = useForm<
		TypedTestOrderingQuestionPayload<'ordering'>
	>({
		mode: 'all',
		resolver: yupResolver(ORDERING_SCHEMA),
		defaultValues: {
			test_id: currentEntity?.id ?? 0,
			orderingOptions: [
				{ option: '', order: 1 },
				{ option: '', order: 2 },
			],
			tags: [],
			weight: NaN,
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'orderingOptions',
		keyName: 'opId',
	});

	const watchTags = watch('tags');
	const watcOrderOptions = watch('orderingOptions');

	useEffect(() => {
		if (addedId !== undefined) {
			setValue('tags', [...watchTags, addedId]);
		}
	}, [addedId]);

	const canRemoveOption = useMemo(() => {
		return watcOrderOptions?.length > 2;
	}, [watcOrderOptions]);

	const onSave = async (form: TypedTestOrderingQuestionPayload<'ordering'>) => {
		try {
			form.orderingOptions = form.orderingOptions.map((or, i) => ({
				...or,
				order: i + 1,
			}));
			await TestQuestionsRequester.instance.create(form);
			displaySnackbar('success', 'Created question successfully');
			goToParent();
		} catch (error) {
			displaySnackbar('error', "Couldn't create question");
		}
	};
	const onSaveWithReset = async (
		form: TypedTestOrderingQuestionPayload<'ordering'>
	) => {
		try {
			form.orderingOptions = form.orderingOptions.map((or, i) => ({
				...or,
				order: i + 1,
			}));
			await TestQuestionsRequester.instance.create(form);
			displaySnackbar('success', 'Created question successfully');
			reset({
				content: '',
				feedback: '',
				orderingOptions: [
					{ option: '', order: 1 },
					{ option: '', order: 2 },
				],
				tags: [],
				weight: NaN,
				test_id: currentEntity?.id ?? 0,
			});
		} catch (error) {
			displaySnackbar('error', "Couldn't create question");
		}
	};

	const { isDirty, isValid, isSubmitting } = formState;

	return (
		<FlexLayout flexDirection="column" px="2.625rem" flex="1">
			<UnsavedChangesGuard formState={formState} />
			<FlexLayout flex="1">
				<Controller
					control={control}
					name="content"
					render={({ field: { value, onChange, ...props } }) => {
						return (
							<>
								<RichTextEditor preset="question-basic"
									disabled={currentEntity.active}
									{...props}
									value={value}
									onChange={onChange}
								/>
							</>
						);
					}}
				/>
			</FlexLayout>

			<Spacer my="1.5rem" />
			<Note>
				Note: Add possible answers in the correct order. We'll present them
				randomly for the end-user.
			</Note>
			<GridLayout gridTemplateColumns={'repeat(1,1fr)'} gridGap="1.875rem">
				<FlexLayout mb="1.875rem" flexDirection="column">
					{fields.map(({ opId, option, order }, index) => {
						return (
							<OrderingOption
								key={opId}
								canRemoveOption={canRemoveOption}
								remove={remove}
								control={control}
								index={index}
								orderIndex={orderIndex}
								setOrderIndex={setOrderIndex}
							/>
						);
					})}
					{!currentEntity.active && (
						<div>
							<Button
								onClick={() => {
									setOrderIndex(orderIndex + 1);
									append({ option: '', order: orderIndex });
								}}
								variant="outlined"
								startIcon={<EdIcon>add</EdIcon>}
							>
								ADD ANSWER
							</Button>
						</div>
					)}
				</FlexLayout>
				<FlexLayout alignItems="center">
					<EdFormControl
						width="5rem"
						type="number"
						disabled={currentEntity.active}
						control={control}
						name="weight"
						label="Weight"
					/>
					<Spacer mx="0.5rem" />
					<Typography fontWeight="600">Points</Typography>
				</FlexLayout>
				<FlexLayout gridGap="1rem">
					<EdFormControl
						disabled={currentEntity.active}
						label="Tags"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<EdIcon mIconType="Regular">local_offer</EdIcon>
								</InputAdornment>
							),
						}}
						SelectProps={{
							multiple: true,
							renderValue: (value) => {
								const values = Array.from(value as number[]);
								return (
									<>
										{values.map((v) => {
											const tag = dropdownTags.find((_) => _.id === v);
											return <>{tag?.name},</>;
										})}
									</>
								);
							},
						}}
						control={control}
						name="tags"
						select
						multiple
						renderValues={(value) => {
							return (
								<>
									<Spacer mb="1rem" />
									<FlexLayout
										// maxWidth={"17rem"}
										overflow="auto"
										py={'0.4rem'}
										flexWrap="wrap"
										gridGap="0.5rem"
									>
										{watchTags &&
											dropdownTags
												.filter((_) => watchTags?.includes(_.id))
												.map((tag) => {
													return (
														<TagChip
															bgColor={tag.theme}
															icon={<EdIcon>local_offer</EdIcon>}
															label={tag.name}
														/>
													);
												})}
									</FlexLayout>
								</>
							);
						}}
					>
						{dropdownTags.map((tag, index) => {
							return (
								<MenuItem value={tag.id} key={`tag-${tag.id}`}>
									<Checkbox
										sx={{ color: tag.theme }}
										checked={watchTags?.includes(tag.id)}
									/>
									<ListItemText primary={tag.name} />
								</MenuItem>
							);
						})}
					</EdFormControl>
					{!currentEntity.active && <AddTag setAddedId={setAddedId} />}
				</FlexLayout>
				<FlexLayout>
					<EdFormControlLabel>Feedback</EdFormControlLabel>
					<Controller
						control={control}
						name="feedback"
						render={({ field: { onChange, value, ...props }, fieldState }) => {
							return (
								<RichTextEditor preset="feedback"
									{...props}
									min_height={200}
									value={value}
									onChange={onChange}
								/>
							);
						}}
					/>
				</FlexLayout>
			</GridLayout>
			{!currentEntity.active && (
				<FlexLayout mt="1.688rem" justifyContent="flex-end">
					<Button
						variant="contained"
						color="warning"
						onClick={() => {
							goToParent();
						}}
					>
						Cancel
					</Button>
					<Spacer mx="0.5rem" />

					<EdButtonMenu
						onClick={handleSubmit(onSave)}
						disabled={isSubmitting || !isValid || !isDirty}
						variant="contained"
						MenuItems={
							<div>
								<MenuItem onClick={handleSubmit(onSaveWithReset)}>
									Save and add another
								</MenuItem>
							</div>
						}
					>
						Save
					</EdButtonMenu>
				</FlexLayout>
			)}
		</FlexLayout>
	);
};

export const Note = styled.div`
	font-size: 1rem;
	font-weight: normal;
	color: ${(props) => props.theme.palette.platinum?.[600]};
	margin-bottom: 1rem;
`;
export default Ordering;
